import { useQuery, useQueries, useMutation } from '@tanstack/react-query'
import { StatementApi as StatementApiClass } from '@pimy-b2cweb/apiclient-customer-stmt'
import { BaseApiConfig, apiClient } from './api-config'

import { store } from '@/stores'
import { selectAuthSessionState } from '@/stores/auth.selectors'
import { StatusEnum } from '@/config'

export const StmtApi = new StatementApiClass(BaseApiConfig, apiClient)
const source = 'B2C'

export const useQueryStmtRecent = () => {
  const { token, status } = selectAuthSessionState(store.getState()) ?? {}
  return useQuery({
    queryKey: ['query-stmt-recent', token, status],
    queryFn: async () => {
      const {
        data: {
          body: { statements, accountNumbers, accountsList },
        },
      } = await StmtApi.recent({
        stmtRecentReqBodyPayload: {
          accountNumbers: [],
        },
      })
      return { statements, accountNumbers, accountsList }
    },
    enabled: !!token && status === StatusEnum.LoggedIn,
  })
}

export const useQueryStmtSoaDocMsCifIdAccNoApi = (
  { country, idType, idNo }: { country: string; idType: string; idNo: number },
  { enabled }: { enabled: boolean }
) => {
  const { token, status } = selectAuthSessionState(store.getState()) ?? {}

  return useQuery({
    queryKey: ['query-stmt-soa-docms-cifid-accno-api', idNo],
    queryFn: async () => {
      const response = await StmtApi.docMsCifIdAccNoApi({
        country,
        idType,
        idNo,
      })
      const docMsCifIdAccNoApiResponse = response.data.body
      return docMsCifIdAccNoApiResponse
    },
    enabled: !!token && status === StatusEnum.LoggedIn && enabled,
  })
}

export const useQueryStmtSoaDocMsSearchApi = (
  {
    category,
    accountNos,
    limit = 10,
    page = 1,
  }: {
    category: number
    accountNos: string[] | undefined
    limit?: number
    page?: number
  },
  { enabled }: { enabled: boolean }
) => {
  const { token, status } = selectAuthSessionState(store.getState()) ?? {}

  const queries = useQueries({
    queries: (accountNos ?? []).map((accountNo) => ({
      queryKey: [
        'query-stmt-soa-docms-search-api',
        category,
        accountNo,
        page,
        limit,
      ],
      keepPreviousData: true,
      queryFn: async () => {
        const response = await StmtApi.docMsSearchApi({
          stmtDocMsSearchReqBodyPayload: {
            category,
            account_no: accountNo,
            limit,
            page,
          },
        })
        const docMsSearchApiResponse = response?.data?.body
        return docMsSearchApiResponse
      },
      enabled:
        !!token && status === StatusEnum.LoggedIn && enabled && !!accountNo,
    })),
  })

  const isLoading = queries.some((q) => q.status === 'loading')
  const isError = queries.some((q) => q.status === 'error')

  const mergedBody = {
    list: queries.flatMap((q) => q.data?.list ?? []),
    count: queries.reduce((sum, q) => sum + (q.data?.count ?? 0), 0),
    page,
    limit,
  }

  return {
    data: mergedBody,
    status: isLoading ? 'loading' : isError ? 'error' : 'success',
    error: queries.find((q) => q.error)?.error,
    queries,
  }
}

export const useMutateStmtSoaDocMsDownloadApi = () => {
  return useMutation({
    mutationFn: async (docId: string) => {
      const response = await StmtApi.queryDocMsDownloadApiByDocId({ docId })
      return response.data.body
    },
  })
}

export const useQueryStmtAccountDetails = (
  { accountNo, idNo }: { accountNo: string; idNo: string },
  { enabled }: { enabled: boolean }
) => {
  const { token, status } = selectAuthSessionState(store.getState()) ?? {}
  return useQuery({
    queryKey: ['query-stmt-account-details', accountNo],
    queryFn: async () => {
      const response = await StmtApi.queryAccountDetails({
        source,
        stmtAccountDetailsReqBodyPayload: { accountNo, idNo },
      })
      const accountHoldings = response.data.body?.accountHoldings
      const AccountYtdContributonList =
        response.data.body?.AccountYtdContributonList
      const navDetails = response.data.body?.navDetails
      return { accountHoldings, AccountYtdContributonList, navDetails }
    },
    enabled: !!token && status === StatusEnum.LoggedIn && enabled,
  })
}

export const useQueryStmtTransactionDetails = (
  {
    accountNo,
    idType,
    idNo,
  }: { accountNo: string; idType: string; idNo: string },
  { enabled }: { enabled: boolean }
) => {
  const { token, status } = selectAuthSessionState(store.getState()) ?? {}
  return useQuery({
    queryKey: ['query-stmt-transaction-details', accountNo],
    queryFn: async () => {
      const response = await StmtApi.queryTransactionDetails({
        source,
        stmtTransactionDetailsReqBodyPayload: { accountNo, idNo, idType },
      })
      const transactionLedger = response.data.body?.transactionLedger
      const isGHRPHR = response.data.body?.isGHRPHR
      return { transactionLedger, isGHRPHR }
    },
    enabled: !!token && status === StatusEnum.LoggedIn && enabled,
  })
}

export const useQueryStmtContributionSummary = (
  { accountNo, idNo }: { accountNo: string; idNo: string },
  { enabled }: { enabled: boolean }
) => {
  const { token, status } = selectAuthSessionState(store.getState()) ?? {}
  return useQuery({
    queryKey: ['query-stmt-contribution-summary', accountNo],
    queryFn: async () => {
      const response = await StmtApi.queryContributionSummary({
        source,
        stmtContributionSummaryReqBodyPayload: { accountNo, idNo },
      })
      const investmentApproach = response.data.body?.investmentApproach
      const accountHoldings = response.data.body?.accountHoldings
      return { accountHoldings, investmentApproach }
    },
    enabled: !!token && status === StatusEnum.LoggedIn && enabled,
  })
}

export const useMultipleContributionQueries = (
  accounts: { accountNo: string; idNo: string }[],
  enabled: boolean
) => {
  const { token, status } = selectAuthSessionState(store.getState()) ?? {}
  return useQueries({
    queries: accounts.map(({ accountNo, idNo }) => ({
      queryKey: ['query-stmt-contribution-summary', accountNo],
      queryFn: async () => {
        const response = await StmtApi.queryContributionSummary({
          source,
          stmtContributionSummaryReqBodyPayload: { accountNo, idNo },
        })
        const investmentApproach = response.data.body?.investmentApproach
        const accountHoldings = response.data.body?.accountHoldings
        const isGHRPHR = response.data.body?.isGHRPHR
        return { accountHoldings, investmentApproach, isGHRPHR }
      },
      enabled: !!token && status === StatusEnum.LoggedIn && enabled,
    })),
  })
}

export const useQueryStmtRspDetails = (
  { accountNo, idNo }: { accountNo: string; idNo: string },
  { enabled }: { enabled: boolean }
) => {
  const { token, status } = selectAuthSessionState(store.getState()) ?? {}
  return useQuery({
    queryKey: ['query-stmt-rsp-details', accountNo],
    queryFn: async () => {
      const response = await StmtApi.queryRspDetails({
        source,
        stmtRspDetailsReqBodyPayload: { accountNo, idNo },
      })
      return response.data.body?.rspDetails
    },
    enabled: !!token && status === StatusEnum.LoggedIn && enabled,
  })
}

export const useQueryStmtRwpDetails = (
  { accountNo, idNo }: { accountNo: string; idNo: string },
  { enabled }: { enabled: boolean }
) => {
  const { token, status } = selectAuthSessionState(store.getState()) ?? {}
  return useQuery({
    queryKey: ['query-stmt-rwp-details', accountNo],
    queryFn: async () => {
      const response = await StmtApi.queryRwpDetails({
        source,
        stmtRwpDetailsReqBodyPayload: { accountNo, idNo },
      })
      return response.data.body?.rwpDetails
    },
    enabled: !!token && status === StatusEnum.LoggedIn && enabled,
  })
}

export const useQueryStmtDistributorDetails = (
  { accountNo, idNo }: { accountNo: string; idNo: string },
  { enabled }: { enabled: boolean }
) => {
  const { token, status } = selectAuthSessionState(store.getState()) ?? {}
  return useQuery({
    queryKey: ['query-stmt-distributor-details', accountNo],
    queryFn: async () => {
      const response = await StmtApi.queryDistributorDetails({
        source,
        stmtDistributorDetailsReqBodyPayload: { accountNo, idNo },
      })
      return response.data.body?.distributorDetails
    },
    enabled: !!token && status === StatusEnum.LoggedIn && enabled,
  })
}

export const useQueryRetirementCalculator = async (payload: {
  amount: number
  currentAge: number
  retiredAge: number
  annReturnsBeforeRetired: number
  annReturnsAfterRetired: number
  annualInflation: number
  annualLifeExpectancy: number
  monthly: boolean
}) => {
  const response = await StmtApi.retirementCalculator({
    stmtRetirementCalculatorReqBodyPayload: payload,
  })

  return response.data.body?.retirementCalculatorResponse
}
